import React from "react"
import PropTypes from "prop-types"
import { Typography, Box } from "@mui/material"
import i18n from "i18n"

import { formatChartNumber } from "./utils/numberFormat"
import OverviewCard, {
  overviewCardValueSx,
  overviewCardHelperTextSx,
} from "./OverviewCard"
import OverviewMetricChip from "./OverviewMetricChip"

/**
 * El backend expone desvío = real imputado − coste esperado (positivo = sobrecoste).
 * En pantalla mostramos el signo invertido: positivo = ahorro, negativo = sobrecoste.
 */
const getAccent = displayDeviation => {
  if (displayDeviation > 0) return "success"
  if (displayDeviation < 0) return "error"
  return "default"
}

const DeviationAtDateCard = ({
  deviationAtDate,
  deviationAtDatePercentage,
  isLoading,
  currency,
}) => {
  const displayDeviation = -deviationAtDate
  const accent = getAccent(displayDeviation)

  const absAmount = formatChartNumber(Math.abs(displayDeviation), 2)
  const amountSign =
    displayDeviation > 0 ? "+" : displayDeviation < 0 ? "−" : ""
  const amountText = `${amountSign}${absAmount} ${currency ?? ""}`.trim()

  const hasRawPercent =
    deviationAtDatePercentage !== null &&
    Number.isFinite(deviationAtDatePercentage)

  const displayPercent = hasRawPercent ? -deviationAtDatePercentage : null

  let percentLine = null
  if (hasRawPercent && Number.isFinite(displayPercent)) {
    const p = displayPercent
    const absP = Math.abs(p)
    const pctSign = p > 0 ? "+" : p < 0 ? "−" : ""
    percentLine = i18n.t("overview.deviationAtDatePercentLine", {
      sign: pctSign,
      number: absP.toFixed(1).replace(".", ","),
    })
  }

  const tooltipTitle = (
    <Box sx={{ maxWidth: 320, whiteSpace: "pre-line" }}>
      {i18n.t("overview.deviationAtDateCardTooltip")}
    </Box>
  )

  return (
    <OverviewCard
      iconPreset="costDeviationAtDate"
      title={i18n.t("overview.deviationAtDateCardTitle")}
      infoText={tooltipTitle}
      loading={isLoading}
      sx={{ height: "100%" }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <Typography sx={overviewCardValueSx}>{amountText}</Typography>
        {displayDeviation !== 0 ? (
          <OverviewMetricChip accent={accent}>
            {displayDeviation > 0
              ? i18n.t("overview.deviationAtDateBadgeSavings")
              : i18n.t("overview.deviationAtDateBadgeOvercost")}
          </OverviewMetricChip>
        ) : null}
      </Box>
      {percentLine ? (
        <Typography variant="body2" sx={overviewCardHelperTextSx}>
          {percentLine}
        </Typography>
      ) : null}
    </OverviewCard>
  )
}

DeviationAtDateCard.propTypes = {
  deviationAtDate: PropTypes.number.isRequired,
  deviationAtDatePercentage: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([null]),
  ]),
  isLoading: PropTypes.bool,
  currency: PropTypes.string,
}

export default DeviationAtDateCard
